import { PartnerPartnerDetails } from 'vopay';
import { useCallback, useRef, useState } from 'react';
import { api } from '@/services/api.config';

/**
 * The details of one partner account this partner created (ID-13788 item 3): identity, program
 * terms, agreement status and — for an Active partner account with an active key — its API
 * credentials.
 */
export default function useFetchPartnerPartnerDetails() {
  const [fetchingPartnerDetails, setFetchingPartnerDetails] = useState(false);
  const [partnerDetails, setPartnerDetails] = useState<PartnerPartnerDetails>();

  /**
   * Only the newest request may write to state. The drawer refetches as the user moves from one
   * partner to the next, and nothing cancels the request already in flight — so without this an
   * earlier response landing last would put one partner's credentials under another's name.
   */
  const latestRequestId = useRef(0);

  const fetchPartnerPartnerDetails = useCallback(async (partnerAccountID: string) => {
    latestRequestId.current += 1;
    const requestId = latestRequestId.current;
    const isLatest = () => latestRequestId.current === requestId;

    try {
      setFetchingPartnerDetails(true);
      const res = await api.fetchPartnerPartnerDetails({ PartnerAccountID: partnerAccountID });
      if (!res.Success) throw new Error(res.ErrorMessage);
      if (isLatest()) setPartnerDetails(res);

      return res;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      if (isLatest()) setFetchingPartnerDetails(false);
    }
  }, []);

  const reset = useCallback(() => {
    // A reset supersedes whatever is in flight, so that response can no longer land on the cleared
    // state — which is what makes closing the drawer and opening another partner safe.
    latestRequestId.current += 1;
    setPartnerDetails(undefined);
    setFetchingPartnerDetails(false);
  }, []);

  return { fetchingPartnerDetails, partnerDetails, fetchPartnerPartnerDetails, reset };
}
